import * as z from 'zod';

export const GetReferralEventRequest = z.object({
  referralEventId: z
    .string()
    .describe('ID of the referral event to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetReferralEventResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Referral event ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the referral event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the referral event.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the referral event was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the referral event was last updated.')
      .optional()
      .nullable(),
  }),
  z.xor([
    z.object({
      referredFriendSignupEvent: z.never().optional(),
      successfulReferralEvent: z.never().optional(),
      actionEvent: z.never().optional(),
      rewardEvent: z.never().optional(),
    }),
    z.object({
      successfulReferralEvent: z.never().optional(),
      actionEvent: z.never().optional(),
      rewardEvent: z.never().optional(),
      referredFriendSignupEvent: z
        .object({
          referredFriendId: z
            .string()
            .describe('ID of the referred friend.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe('Event triggered when a referred friend signs up.'),
    }),
    z.object({
      referredFriendSignupEvent: z.never().optional(),
      actionEvent: z.never().optional(),
      rewardEvent: z.never().optional(),
      successfulReferralEvent: z
        .object({
          referredFriendId: z
            .string()
            .describe('ID of the referred friend.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          referringCustomerId: z
            .string()
            .describe('ID of the referring customer.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
        })
        .describe(
          'Event triggered when a referral is successful. For example, customer places and pays for an order.'
        ),
    }),
    z.object({
      referredFriendSignupEvent: z.never().optional(),
      successfulReferralEvent: z.never().optional(),
      rewardEvent: z.never().optional(),
      actionEvent: z
        .object({
          referredFriendId: z
            .string()
            .describe('ID of the referred friend.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          referringCustomerId: z
            .string()
            .describe('ID of the referring customer.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          trigger: z
            .object({
              appId: z
                .string()
                .describe('ID of the app that triggered the event.')
                .optional(),
              activityType: z
                .string()
                .describe('Type of activity that triggered the event.')
                .optional(),
            })
            .describe('Trigger for the action.')
            .optional(),
          amount: z
            .string()
            .describe('Amount associated with the action.')
            .optional()
            .nullable(),
          currency: z
            .string()
            .describe('Currency of the amount.')
            .optional()
            .nullable(),
          orderId: z
            .string()
            .describe('ID of the associated order.')
            .optional()
            .nullable(),
        })
        .describe(
          'Event triggered when an action is performed. For example, placing an order.'
        ),
    }),
    z.object({
      referredFriendSignupEvent: z.never().optional(),
      successfulReferralEvent: z.never().optional(),
      actionEvent: z.never().optional(),
      rewardEvent: z
        .intersection(
          z.object({
            referralRewardId: z
              .string()
              .describe('ID of the referral reward.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            rewardType: z
              .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
              .describe('Type of reward.')
              .optional(),
          }),
          z.xor([
            z.object({
              rewardedReferringCustomerId: z.never().optional(),
              rewardedReferredFriendId: z.never().optional(),
            }),
            z.object({
              rewardedReferredFriendId: z.never().optional(),
              rewardedReferringCustomerId: z
                .string()
                .describe('ID of the rewarded referring customer.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
            z.object({
              rewardedReferringCustomerId: z.never().optional(),
              rewardedReferredFriendId: z
                .string()
                .describe('ID of the rewarded referred friend.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
            }),
          ])
        )
        .describe('Event triggered when a reward is given.'),
    }),
  ])
);
export const QueryReferralEventRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          referredFriendSignupEvent: z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          successfulReferralEvent: z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          actionEvent: z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          rewardEvent: z
            .object({
              $eq: z.any(),
              $exists: z.boolean(),
              $gt: z.any(),
              $gte: z.any(),
              $hasAll: z.array(z.any()),
              $hasSome: z.array(z.any()),
              $in: z.array(z.any()),
              $lt: z.any(),
              $lte: z.any(),
              $ne: z.any(),
              $nin: z.array(z.any()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                'referredFriendSignupEvent',
                'successfulReferralEvent',
                'actionEvent',
                'rewardEvent',
                '_createdDate',
                '_updatedDate',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('Query to filter referral events.'),
});
export const QueryReferralEventResponse = z.object({
  referralEvents: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Referral event ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the referral event is updated.\nTo prevent conflicting changes, the current revision must be passed when updating the referral event.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the referral event was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the referral event was last updated.')
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({
            referredFriendSignupEvent: z.never().optional(),
            successfulReferralEvent: z.never().optional(),
            actionEvent: z.never().optional(),
            rewardEvent: z.never().optional(),
          }),
          z.object({
            successfulReferralEvent: z.never().optional(),
            actionEvent: z.never().optional(),
            rewardEvent: z.never().optional(),
            referredFriendSignupEvent: z
              .object({
                referredFriendId: z
                  .string()
                  .describe('ID of the referred friend.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe('Event triggered when a referred friend signs up.'),
          }),
          z.object({
            referredFriendSignupEvent: z.never().optional(),
            actionEvent: z.never().optional(),
            rewardEvent: z.never().optional(),
            successfulReferralEvent: z
              .object({
                referredFriendId: z
                  .string()
                  .describe('ID of the referred friend.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                referringCustomerId: z
                  .string()
                  .describe('ID of the referring customer.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
              })
              .describe(
                'Event triggered when a referral is successful. For example, customer places and pays for an order.'
              ),
          }),
          z.object({
            referredFriendSignupEvent: z.never().optional(),
            successfulReferralEvent: z.never().optional(),
            rewardEvent: z.never().optional(),
            actionEvent: z
              .object({
                referredFriendId: z
                  .string()
                  .describe('ID of the referred friend.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                referringCustomerId: z
                  .string()
                  .describe('ID of the referring customer.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                trigger: z
                  .object({
                    appId: z
                      .string()
                      .describe('ID of the app that triggered the event.')
                      .optional(),
                    activityType: z
                      .string()
                      .describe('Type of activity that triggered the event.')
                      .optional(),
                  })
                  .describe('Trigger for the action.')
                  .optional(),
                amount: z
                  .string()
                  .describe('Amount associated with the action.')
                  .optional()
                  .nullable(),
                currency: z
                  .string()
                  .describe('Currency of the amount.')
                  .optional()
                  .nullable(),
                orderId: z
                  .string()
                  .describe('ID of the associated order.')
                  .optional()
                  .nullable(),
              })
              .describe(
                'Event triggered when an action is performed. For example, placing an order.'
              ),
          }),
          z.object({
            referredFriendSignupEvent: z.never().optional(),
            successfulReferralEvent: z.never().optional(),
            actionEvent: z.never().optional(),
            rewardEvent: z
              .intersection(
                z.object({
                  referralRewardId: z
                    .string()
                    .describe('ID of the referral reward.')
                    .regex(
                      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                      'Must be a valid GUID'
                    )
                    .optional(),
                  rewardType: z
                    .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
                    .describe('Type of reward.')
                    .optional(),
                }),
                z.xor([
                  z.object({
                    rewardedReferringCustomerId: z.never().optional(),
                    rewardedReferredFriendId: z.never().optional(),
                  }),
                  z.object({
                    rewardedReferredFriendId: z.never().optional(),
                    rewardedReferringCustomerId: z
                      .string()
                      .describe('ID of the rewarded referring customer.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                  z.object({
                    rewardedReferringCustomerId: z.never().optional(),
                    rewardedReferredFriendId: z
                      .string()
                      .describe('ID of the rewarded referred friend.')
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      ),
                  }),
                ])
              )
              .describe('Event triggered when a reward is given.'),
          }),
        ])
      )
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Metadata for the paginated results.')
    .optional(),
});
export const GetReferralStatisticsRequest = z.object({});
export const GetReferralStatisticsResponse = z.object({
  totalSignUpsCompleted: z
    .number()
    .int()
    .describe('Total number of sign-ups completed by referred friends.')
    .optional(),
  totalActionsCompleted: z
    .number()
    .int()
    .describe('Total number of actions completed by referred friends.')
    .optional(),
  totalAmountGenerated: z
    .string()
    .describe('Total amount of purchases made by referred friends.')
    .optional(),
});
export const QueryReferringCustomerTotalsRequest = z.object({
  options: z
    .object({
      query: z
        .intersection(
          z.object({
            filter: z
              .record(z.string(), z.any())
              .describe(
                'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
              )
              .optional()
              .nullable(),
            sort: z
              .array(
                z.object({
                  fieldName: z
                    .string()
                    .describe('Name of the field to sort by.')
                    .max(512)
                    .optional(),
                  order: z.enum(['ASC', 'DESC']).optional(),
                })
              )
              .max(5)
              .optional(),
          }),
          z.xor([
            z.object({ cursorPaging: z.never().optional() }),
            z.object({
              cursorPaging: z
                .object({
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items to return in the results.'
                    )
                    .min(0)
                    .max(100)
                    .optional()
                    .nullable(),
                  cursor: z
                    .string()
                    .describe(
                      "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                    )
                    .max(16000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
                ),
            }),
          ])
        )
        .describe('Query to filter referring customer totals.')
        .optional(),
      contactIds: z.array(z.string()).optional(),
    })
    .optional(),
});
export const QueryReferringCustomerTotalsResponse = z.object({
  referringCustomerTotals: z
    .array(
      z.object({
        referringCustomerId: z
          .string()
          .describe('ID of the referring customer.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        contactId: z
          .string()
          .describe('Contact ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        lastSuccessfulReferral: z
          .date()
          .describe('Date and time of the last successful referral.')
          .optional()
          .nullable(),
        totalSuccessfulReferrals: z
          .number()
          .int()
          .describe(
            'Total number of successful referrals made by this customer.'
          )
          .optional(),
        totalAmountGenerated: z
          .string()
          .describe(
            'Total amount of revenue generated by friends referred by this customer.'
          )
          .optional(),
        lastFriendAction: z
          .date()
          .describe('Date and time of the last friend action.')
          .optional()
          .nullable(),
        totalFriendsWithActions: z
          .number()
          .int()
          .describe('Number of friends who have completed actions.')
          .optional(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const QueryReferredFriendActionsRequest = z.object({
  options: z
    .object({
      query: z
        .intersection(
          z.object({
            filter: z
              .record(z.string(), z.any())
              .describe(
                'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
              )
              .optional()
              .nullable(),
            sort: z
              .array(
                z.object({
                  fieldName: z
                    .string()
                    .describe('Name of the field to sort by.')
                    .max(512)
                    .optional(),
                  order: z.enum(['ASC', 'DESC']).optional(),
                })
              )
              .max(5)
              .optional(),
          }),
          z.xor([
            z.object({ cursorPaging: z.never().optional() }),
            z.object({
              cursorPaging: z
                .object({
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items to return in the results.'
                    )
                    .min(0)
                    .max(100)
                    .optional()
                    .nullable(),
                  cursor: z
                    .string()
                    .describe(
                      "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                    )
                    .max(16000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).'
                ),
            }),
          ])
        )
        .describe('Query to filter referred friend actions.')
        .optional(),
      contactIds: z.array(z.string()).optional(),
    })
    .optional(),
});
export const QueryReferredFriendActionsResponse = z.object({
  referredFriendActions: z
    .array(
      z.intersection(
        z.object({
          referredFriendId: z
            .string()
            .describe('Referred friend ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          contactId: z
            .string()
            .describe('Contact ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          trigger: z
            .object({
              appId: z
                .string()
                .describe('ID of the app that triggered the event.')
                .optional(),
              activityType: z
                .string()
                .describe('Type of activity that triggered the event.')
                .optional(),
            })
            .describe('Trigger for the first action.')
            .optional(),
          actionDate: z
            .date()
            .describe('Date and time of the first action.')
            .optional()
            .nullable(),
          rewardType: z
            .enum(['UNKNOWN', 'COUPON', 'LOYALTY_POINTS', 'NOTHING'])
            .describe('Type of issued reward.')
            .optional(),
          totalActions: z
            .number()
            .int()
            .describe('Number of actions completed.')
            .optional(),
          totalAmountSpent: z
            .string()
            .describe('Total amount spent by this referred friend.')
            .optional(),
          signupDate: z
            .date()
            .describe('Date and time of friend signup.')
            .optional()
            .nullable(),
        }),
        z.xor([
          z.object({
            coupon: z.never().optional(),
            loyaltyPoints: z.never().optional(),
          }),
          z.object({
            loyaltyPoints: z.never().optional(),
            coupon: z
              .object({
                _id: z
                  .string()
                  .describe(
                    'Coupon ID. Example: `8934b045-7052-4a90-be2b-832c70afc9da`.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                code: z
                  .string()
                  .describe(
                    'The code that customers can use to apply the coupon. Example: `6RFD2A3HSPXW`.'
                  )
                  .optional(),
                status: z
                  .enum(['UNKNOWN', 'ACTIVE', 'APPLIED', 'DELETED'])
                  .describe('Current status of the coupon.')
                  .optional(),
                couponSpecification: z
                  .intersection(
                    z.object({
                      name: z
                        .string()
                        .describe('Coupon name.')
                        .min(1)
                        .max(50)
                        .optional(),
                      discountType: z
                        .enum([
                          'UNKNOWN',
                          'FIXED_AMOUNT',
                          'PERCENTAGE',
                          'FREE_SHIPPING',
                        ])
                        .describe('Coupon discount type.')
                        .optional(),
                      limitedToOneItem: z
                        .boolean()
                        .describe(
                          'Whether the coupon is limited to one item.\nIf `true` and a customer pays for multiple items, the discount applies to only the lowest priced item.\nCoupons with a bookings `scope.namespace` are always limited to one item.'
                        )
                        .optional()
                        .nullable(),
                      appliesToSubscriptions: z
                        .boolean()
                        .describe(
                          'Whether the coupon applies to subscription products.'
                        )
                        .optional()
                        .nullable(),
                      discountedCycleCount: z
                        .number()
                        .int()
                        .describe(
                          'Specifies the amount of discounted cycles for a subscription item.\n\n- Can only be set when `scope.namespace = pricingPlans`.\n- If `discountedCycleCount` is empty, the coupon applies to all available cycles.\n- `discountedCycleCount` is ignored if `appliesToSubscriptions = true`.\n\nMax: `999`'
                        )
                        .optional()
                        .nullable(),
                    }),
                    z.intersection(
                      z.xor([
                        z.object({
                          fixedAmountOptions: z.never().optional(),
                          percentageOptions: z.never().optional(),
                        }),
                        z.object({
                          percentageOptions: z.never().optional(),
                          fixedAmountOptions: z
                            .object({
                              amount: z
                                .number()
                                .describe(
                                  'Amount of the discount as a fixed value.'
                                )
                                .min(0.01)
                                .optional(),
                            })
                            .describe('Options for fixed amount discount.'),
                        }),
                        z.object({
                          fixedAmountOptions: z.never().optional(),
                          percentageOptions: z
                            .object({
                              percentage: z
                                .number()
                                .describe('Percentage of discount.')
                                .min(0)
                                .max(100)
                                .optional(),
                            })
                            .describe('Options for percentage discounts.'),
                        }),
                      ]),
                      z.xor([
                        z.object({
                          minimumSubtotal: z.never().optional(),
                          scope: z.never().optional(),
                        }),
                        z.object({
                          scope: z.never().optional(),
                          minimumSubtotal: z
                            .number()
                            .describe(
                              'Limit the coupon to carts with a subtotal above this number.'
                            ),
                        }),
                        z.object({
                          minimumSubtotal: z.never().optional(),
                          scope: z
                            .object({
                              namespace: z
                                .string()
                                .describe(
                                  'Scope namespace (Wix Stores, Wix Bookings, Wix Events, Wix Pricing Plans)'
                                )
                                .optional(),
                              group: z
                                .object({
                                  name: z
                                    .string()
                                    .describe('Name of the group.')
                                    .optional(),
                                  entityId: z
                                    .string()
                                    .describe('Entity ID of the group.')
                                    .optional()
                                    .nullable(),
                                })
                                .describe(
                                  "Coupon scope's applied group, for example, Event or ticket in Wix Events."
                                )
                                .optional(),
                            })
                            .describe(
                              'Specifies the type of line items this coupon will apply to. See [valid scope values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values).'
                            ),
                        }),
                      ])
                    )
                  )
                  .describe('Detailed specifications of the coupon.')
                  .optional(),
              })
              .describe('Coupon reward type options.'),
          }),
          z.object({
            coupon: z.never().optional(),
            loyaltyPoints: z
              .object({
                transactionId: z
                  .string()
                  .describe('Loyalty transaction ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                amount: z
                  .number()
                  .int()
                  .describe('The number of loyalty points awarded.')
                  .optional(),
              })
              .describe('Loyalty points reward type options.'),
          }),
        ])
      )
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
